import { useGetPackagesQuery } from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { DocumentPackageType, DocumentPackageTypeLabels } from '@consigli/types';

import { Field, Fieldset, Select } from '@/components';
import { FileUpload } from '@/components/file-upload';

export enum CommentsMode {
  NO_COMMENTS = 'NO_COMMENTS',
  EMBEDDED_IN_VERSION_A = 'EMBEDDED_IN_VERSION_A',
  SEPARATE_UPLOAD = 'SEPARATE_UPLOAD',
  BOTH = 'BOTH',
}

export enum PackageInputMode {
  SELECT_EXISTING = 'SELECT_EXISTING',
  UPLOAD_NEW = 'UPLOAD_NEW',
}

const VERSION_COMPARE_SOURCE_TYPES = new Set([
  DocumentPackageType.OFFER,
  DocumentPackageType.TENDER_DOCUMENTS,
  DocumentPackageType.RFP_RFQ_REVIEW,
  DocumentPackageType.MANAGEMENT_OPERATIONS_MAINTENANCE,
  DocumentPackageType.DESIGN_PACKAGE_REVIEW,
  DocumentPackageType.CONTRACT_REVIEW,
]);

const COMMENTS_MODE_LABELS: Record<CommentsMode, string> = {
  [CommentsMode.NO_COMMENTS]: 'No Comments',
  [CommentsMode.EMBEDDED_IN_VERSION_A]: 'Embedded in Version A',
  [CommentsMode.SEPARATE_UPLOAD]: 'Separate Upload',
  [CommentsMode.BOTH]: 'Both',
};

type PackageOption = { label: string; value: string };

interface VersionInputProps {
  label: string;
  description: string;
  mode: PackageInputMode;
  packageId: string | null;
  files: File[];
  packageOptions: PackageOption[];
  isLoading: boolean;
  selectPlaceholder: string;
  packageError?: string;
  onModeChange: (mode: PackageInputMode) => void;
  onPackageIdChange: (id: string | null) => void;
  onFilesChange: (files: File[]) => void;
}

const VersionInput = ({
  label,
  description,
  mode,
  packageId,
  files,
  packageOptions,
  isLoading,
  selectPlaceholder,
  packageError,
  onModeChange,
  onPackageIdChange,
  onFilesChange,
}: VersionInputProps) => {
  const selectedOption = packageOptions.find((opt) => opt.value === packageId) ?? null;

  return (
    <Field label={label} htmlFor={`${label}-input`} required error={packageError}>
      <p className="mb-2 text-sm text-gray-500">{description}</p>
      <div className="mb-2 flex gap-2">
        <Button
          type="button"
          variant={mode === PackageInputMode.SELECT_EXISTING ? 'default' : 'outline'}
          onClick={() => onModeChange(PackageInputMode.SELECT_EXISTING)}
        >
          Select existing
        </Button>
        <Button
          type="button"
          variant={mode === PackageInputMode.UPLOAD_NEW ? 'default' : 'outline'}
          onClick={() => onModeChange(PackageInputMode.UPLOAD_NEW)}
        >
          Upload new
        </Button>
      </div>
      {mode === PackageInputMode.SELECT_EXISTING ? (
        <Select
          options={packageOptions}
          placeholder={isLoading ? 'Loading...' : selectPlaceholder}
          value={selectedOption}
          onValueChange={(option) => onPackageIdChange(option?.value ?? null)}
          getOptionLabel={(option) => option?.label ?? ''}
          getOptionValue={(option) => option?.value ?? ''}
          className="w-full"
        />
      ) : (
        <FileUpload setFiles={onFilesChange} placeholder="Upload documents" files={files} />
      )}
    </Field>
  );
};

interface VersionCompareSectionProps {
  projectId: number;
  primaryMode: PackageInputMode;
  secondaryMode: PackageInputMode;
  primaryPackageId: string | null;
  secondaryPackageId: string | null;
  primaryFiles: File[];
  secondaryFiles: File[];
  commentsMode: CommentsMode;
  commentRegisterFiles: File[];
  errors?: Record<string, string>;
  onPrimaryModeChange: (mode: PackageInputMode) => void;
  onSecondaryModeChange: (mode: PackageInputMode) => void;
  onPrimaryPackageIdChange: (id: string | null) => void;
  onSecondaryPackageIdChange: (id: string | null) => void;
  onPrimaryFilesChange: (files: File[]) => void;
  onSecondaryFilesChange: (files: File[]) => void;
  onCommentsModeChange: (mode: CommentsMode) => void;
  onCommentRegisterFilesChange: (files: File[]) => void;
}

export const VersionCompareSection = ({
  projectId,
  primaryMode,
  secondaryMode,
  primaryPackageId,
  secondaryPackageId,
  primaryFiles,
  secondaryFiles,
  commentsMode,
  commentRegisterFiles,
  errors = {},
  onPrimaryModeChange,
  onSecondaryModeChange,
  onPrimaryPackageIdChange,
  onSecondaryPackageIdChange,
  onPrimaryFilesChange,
  onSecondaryFilesChange,
  onCommentsModeChange,
  onCommentRegisterFilesChange,
}: VersionCompareSectionProps) => {
  const { data: allPackages = [], isLoading } = useGetPackagesQuery({ projectId });

  const packageOptions = allPackages
    .filter((pkg) => VERSION_COMPARE_SOURCE_TYPES.has(pkg.type as DocumentPackageType))
    .map((pkg) => ({
      label: `[${DocumentPackageTypeLabels[pkg.type as DocumentPackageType]}] ${pkg.name}`,
      value: pkg.id,
    }));

  const showCommentUpload =
    commentsMode === CommentsMode.SEPARATE_UPLOAD || commentsMode === CommentsMode.BOTH;

  return (
    <Fieldset legend="Version Compare">
      <p className="text-sm text-gray-500">
        For each version, select an existing package or upload a new set of documents. Version A is
        the original; Version B is the revised.
      </p>
      <VersionInput
        label="Version A (original)"
        description="The original version to compare from."
        mode={primaryMode}
        packageId={primaryPackageId}
        files={primaryFiles}
        packageOptions={packageOptions}
        isLoading={isLoading}
        selectPlaceholder="Select Version A package"
        packageError={errors.versionComparePrimaryPackageId}
        onModeChange={onPrimaryModeChange}
        onPackageIdChange={onPrimaryPackageIdChange}
        onFilesChange={onPrimaryFilesChange}
      />
      <VersionInput
        label="Version B (revised)"
        description="The revised version to compare against."
        mode={secondaryMode}
        packageId={secondaryPackageId}
        files={secondaryFiles}
        packageOptions={packageOptions}
        isLoading={isLoading}
        selectPlaceholder="Select Version B package"
        packageError={errors.versionCompareSecondaryPackageId}
        onModeChange={onSecondaryModeChange}
        onPackageIdChange={onSecondaryPackageIdChange}
        onFilesChange={onSecondaryFilesChange}
      />
      <Field label="Comments" htmlFor="comments-mode" error={errors.versionCompareCommentsMode}>
        <div className="flex gap-2 flex-wrap">
          {Object.values(CommentsMode).map((mode) => (
            <Button
              key={mode}
              type="button"
              variant={commentsMode === mode ? 'default' : 'outline'}
              onClick={() => onCommentsModeChange(mode)}
            >
              {COMMENTS_MODE_LABELS[mode]}
            </Button>
          ))}
        </div>
      </Field>
      {showCommentUpload && (
        <Field label="Comment Register" htmlFor="comment-register-input">
          <FileUpload
            setFiles={onCommentRegisterFilesChange}
            placeholder="Upload comment register"
            files={commentRegisterFiles}
          />
        </Field>
      )}
    </Fieldset>
  );
};
